"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t as fill } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";

type FormatNum = (value: number, options?: Intl.NumberFormatOptions) => string;

// Each captured group is a single token — digits with at most one decimal
// mark, "." or "," — so parseNumberInput can interpret it unambiguously
// once the fraction/percent shape itself has been recognized.
const FRACTION_RE = /^(\d+(?:[.,]\d+)?)\s*\/\s*(\d+(?:[.,]\d+)?)$/;
const PERCENT_RE = /^(\d+(?:[.,]\d+)?)\s*%$/;

/** English strings. A translated content file overrides these through `ui`. */
export const PROBABILITY_CALCULATOR_STRINGS = {
  singleEventHeading: "Single event",
  favorableOutcomes: "Favorable outcomes",
  totalOutcomes: "Total outcomes",
  singleInvalid:
    "Enter whole numbers with favorable ≤ total and total greater than zero.",
  headlineP: "P = ",
  percentOpen: " (",
  percentClose: "%)",
  singleFormula: "P(event) = favorable ÷ total = {favorable} ÷ {total} = {p}",
  singlePercent: "As a percentage: {p} × 100 = {percent}%",
  oddsLine:
    "Odds in favor: {favorable} favorable to {unfavorable} unfavorable = {odds}",
  oddsCertain: "{favorable} : 0 (certain)",
  oddsColon: " : ",
  twoEventsHeading: "Two independent events — AND / OR",
  paLabel: "P(A)",
  pbLabel: "P(B)",
  paPlaceholderTwo: "50% or 1/2",
  pbPlaceholder: "1/6 or 0.1667",
  headlineAndPrefix: "P(A and B) = ",
  andWorkingLine1: "P(A and B) = P(A) × P(B)",
  andWorkingLine2Prefix: "= ",
  andWorkingLine2Mid: " × ",
  andWorkingLine2Equals: " = ",
  headlineOrPrefix: "P(A or B) = ",
  orWorkingLine1: "P(A or B) = P(A) + P(B) − P(A) × P(B)",
  orWorkingLine2Prefix: "= ",
  orWorkingLine2Mid: " + ",
  orWorkingLine2Minus: " − ",
  orWorkingLine2Equals: " = ",
  independenceNote:
    "Honest note: these formulas assume A and B are independent — one happening does not change the other's probability. For dependent events (like drawing cards without replacement) they do not apply.",
  twoEventsInvalid:
    "Enter each probability as a decimal (0.5), a percentage (50%), or a fraction (1/2). Bare numbers above 1 are read as percentages.",
  complementHeading: "Complement — P(not A)",
  paPlaceholderComplement: "30% or 3/10",
  headlineNotPrefix: "P(not A) = ",
  notWorkingLine1: "P(not A) = 1 − P(A)",
  notWorkingLine2Prefix: "= 1 − ",
  notWorkingLine2Equals: " = ",
  complementInvalid:
    "Enter the probability as a decimal (0.3), a percentage (30%), or a fraction (3/10).",
} as const;

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

function fmt(n: number, formatNum: FormatNum): string {
  const rounded = Math.round(n * 10_000) / 10_000;
  return formatNum(rounded, { maximumFractionDigits: 4 });
}

function parsePlainNumber(value: string): number | null {
  const n = parseNumberInput(value);
  if (n === null || n < 0) {
    return null;
  }
  if (n <= 1) {
    return n;
  }
  if (n <= 100) {
    return n / 100;
  }
  return null;
}

/**
 * Parses "0.5", "50%", or "1/2" into a probability between 0 and 1.
 * Bare numbers above 1 (up to 100) are treated as percentages.
 */
function parseProbability(raw: string): number | null {
  const value = raw.trim();
  const fraction = value.match(FRACTION_RE);
  if (fraction) {
    const num = parseNumberInput(fraction[1]);
    const den = parseNumberInput(fraction[2]);
    if (num === null || den === null || den === 0) {
      return null;
    }
    const p = num / den;
    return p <= 1 ? p : null;
  }
  const percent = value.match(PERCENT_RE);
  if (percent) {
    const pct = parseNumberInput(percent[1]);
    if (pct === null) {
      return null;
    }
    const p = pct / 100;
    return p <= 1 ? p : null;
  }
  return parsePlainNumber(value);
}

function parseCount(value: string): number | null {
  const n = parseIntegerInput(value);
  if (n === null || n < 0) {
    return null;
  }
  return n;
}

function oddsString(
  favorable: number,
  unfavorable: number,
  strings: typeof PROBABILITY_CALCULATOR_STRINGS
): string {
  if (unfavorable === 0) {
    return fill(strings.oddsCertain, { favorable });
  }
  const g = gcd(favorable, unfavorable);
  if (g <= 1) {
    return `${favorable}${strings.oddsColon}${unfavorable}`;
  }
  return `${favorable / g}${strings.oddsColon}${unfavorable / g}`;
}

function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <label className="block font-medium text-foreground text-sm" htmlFor={id}>
        {label}
      </label>
      <input
        className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type="text"
        value={value}
      />
    </div>
  );
}

function ResultBlock({
  headline,
  working,
}: {
  headline: string;
  working: string[];
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-2xl text-foreground">{headline}</p>
      <div className="space-y-1 rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
        {working.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function SingleEventSection({
  strings,
}: {
  strings: typeof PROBABILITY_CALCULATOR_STRINGS;
}) {
  const formatNum = useToolNumber();
  const [favorable, setFavorable] = useState("1");
  const [total, setTotal] = useState("6");
  const f = parseCount(favorable);
  const t = parseCount(total);
  const valid = f !== null && t !== null && t > 0 && f <= t;
  const p = valid ? f / t : 0;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.singleEventHeading}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <TextField
          id="single-favorable"
          label={strings.favorableOutcomes}
          onChange={setFavorable}
          value={favorable}
        />
        <TextField
          id="single-total"
          label={strings.totalOutcomes}
          onChange={setTotal}
          value={total}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={`${strings.headlineP}${fmt(p, formatNum)}${strings.percentOpen}${fmt(p * 100, formatNum)}${strings.percentClose}`}
          working={[
            fill(strings.singleFormula, {
              favorable: f,
              total: t,
              p: fmt(p, formatNum),
            }),
            fill(strings.singlePercent, {
              p: fmt(p, formatNum),
              percent: fmt(p * 100, formatNum),
            }),
            fill(strings.oddsLine, {
              favorable: f,
              unfavorable: t - f,
              odds: oddsString(f, t - f, strings),
            }),
          ]}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">{strings.singleInvalid}</p>
      )}
    </section>
  );
}

function TwoEventsSection({
  strings,
}: {
  strings: typeof PROBABILITY_CALCULATOR_STRINGS;
}) {
  const formatNum = useToolNumber();
  const [aInput, setAInput] = useState("50%");
  const [bInput, setBInput] = useState("1/6");
  const pa = parseProbability(aInput);
  const pb = parseProbability(bInput);
  const valid = pa !== null && pb !== null;
  const both = valid ? pa * pb : 0;
  const either = valid ? pa + pb - pa * pb : 0;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.twoEventsHeading}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <TextField
          id="two-a"
          label={strings.paLabel}
          onChange={setAInput}
          placeholder={strings.paPlaceholderTwo}
          value={aInput}
        />
        <TextField
          id="two-b"
          label={strings.pbLabel}
          onChange={setBInput}
          placeholder={strings.pbPlaceholder}
          value={bInput}
        />
      </div>
      {valid && (
        <>
          <ResultBlock
            headline={`${strings.headlineAndPrefix}${fmt(both, formatNum)}${strings.percentOpen}${fmt(both * 100, formatNum)}${strings.percentClose}`}
            working={[
              strings.andWorkingLine1,
              `${strings.andWorkingLine2Prefix}${fmt(pa, formatNum)}${strings.andWorkingLine2Mid}${fmt(pb, formatNum)}${strings.andWorkingLine2Equals}${fmt(both, formatNum)}`,
            ]}
          />
          <ResultBlock
            headline={`${strings.headlineOrPrefix}${fmt(either, formatNum)}${strings.percentOpen}${fmt(either * 100, formatNum)}${strings.percentClose}`}
            working={[
              strings.orWorkingLine1,
              `${strings.orWorkingLine2Prefix}${fmt(pa, formatNum)}${strings.orWorkingLine2Mid}${fmt(pb, formatNum)}${strings.orWorkingLine2Minus}${fmt(both, formatNum)}${strings.orWorkingLine2Equals}${fmt(either, formatNum)}`,
            ]}
          />
          <p className="text-muted-foreground text-sm">
            {strings.independenceNote}
          </p>
        </>
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.twoEventsInvalid}
        </p>
      )}
    </section>
  );
}

function ComplementSection({
  strings,
}: {
  strings: typeof PROBABILITY_CALCULATOR_STRINGS;
}) {
  const formatNum = useToolNumber();
  const [aInput, setAInput] = useState("30%");
  const pa = parseProbability(aInput);
  const valid = pa !== null;
  const not = valid ? 1 - pa : 0;

  return (
    <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
      <h2 className="font-semibold text-foreground text-lg">
        {strings.complementHeading}
      </h2>
      <div className="flex flex-wrap items-end gap-6">
        <TextField
          id="comp-a"
          label={strings.paLabel}
          onChange={setAInput}
          placeholder={strings.paPlaceholderComplement}
          value={aInput}
        />
      </div>
      {valid && (
        <ResultBlock
          headline={`${strings.headlineNotPrefix}${fmt(not, formatNum)}${strings.percentOpen}${fmt(not * 100, formatNum)}${strings.percentClose}`}
          working={[
            strings.notWorkingLine1,
            `${strings.notWorkingLine2Prefix}${fmt(pa, formatNum)}${strings.notWorkingLine2Equals}${fmt(not, formatNum)}`,
          ]}
        />
      )}
      {!valid && (
        <p className="text-muted-foreground text-sm">
          {strings.complementInvalid}
        </p>
      )}
    </section>
  );
}

export default function ProbabilityCalculatorWidget() {
  const strings = useToolUi(PROBABILITY_CALCULATOR_STRINGS);
  return (
    <>
      <SingleEventSection strings={strings} />
      <TwoEventsSection strings={strings} />
      <ComplementSection strings={strings} />
    </>
  );
}
